import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  sectionMargins,
  brandColours,
  fontWeights,
  headingStyles,
  fontSize,
  maxBreakpointQuery,
} from '../styles';
import { Container, Svg, Heading, HtmlContent } from './ui';
import flowerEyeIcon from '../images/flower-eye-icon.svg';
import { stripParagraphs } from '../utils';

const StyledIconContent = styled.section`
  ${sectionMargins()};
  text-align: center;
`;

const StyledIcon = styled(Svg)`
  margin-bottom: 8px;
  width: 75px;
  fill: ${brandColours.primary};

  ${minBreakpointQuery.large`
    margin-bottom: 12px;
  `}
`;

const commonHeadingStyles = () => {
  return css`
    margin-bottom: 20px;

    ${minBreakpointQuery.large`
      margin-bottom: 25px;
    `}

    b {
      font-weight: ${fontWeights.regular};
    }
  `;
};

const StyledHeading = styled(Heading)`
  ${commonHeadingStyles()};
`;

const StyledHeadingAsParagraph = styled.p`
  ${({ alt }) => headingStyles(alt)};
  ${commonHeadingStyles()};
`;

const StyledSubHeading = styled.h3`
  ${commonHeadingStyles()};
  font-weight: ${fontWeights.medium};
  ${fontSize(17)};

  ${minBreakpointQuery.small`
    ${fontSize(19)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(21)};
  `}
`;

const StyledContent = styled.div`
  ${({ hasSubHeading }) => {
    if (hasSubHeading) {
      return css`
        p {
          ${fontSize(15)};

          ${minBreakpointQuery.small`
            ${fontSize(16)};
          `}
        }
      `;
    }
  }}
`;

const StyledContentText = styled(HtmlContent)`
  ${({ mobileReadMore, showMoreContent }) => {
    if (mobileReadMore && !showMoreContent) {
      return css`
        ${maxBreakpointQuery.tsmall`
          max-height: 100px;
          overflow: hidden;
        `}
      `;
    }
  }}
`;

const StyledContentButton = styled.button`
  ${maxBreakpointQuery.tsmall`
    margin-top: 8px;
    text-decoration: underline;
    text-underline-offset: 2px;
  `}

  ${minBreakpointQuery.tsmall`
    display: none;
  `}
`;

const IconContent = ({
  heading,
  subHeading,
  text,
  htmlId,
  headingAsParagraph,
  mobileReadMore,
  ...props
}) => {
  const HeadingComponent = headingAsParagraph
    ? StyledHeadingAsParagraph
    : StyledHeading;

  const [showMoreContent, setShowMoreContent] = useState(false);

  return (
    <StyledIconContent {...props}>
      <Container narrow={true}>
        <StyledIcon image={flowerEyeIcon} />
        <HeadingComponent
          dangerouslySetInnerHTML={{ __html: stripParagraphs(heading) }}
          alt={!subHeading}
          id={htmlId}
        />
        {subHeading && <StyledSubHeading>{subHeading}</StyledSubHeading>}
        {text && (
          <StyledContent hasSubHeading={subHeading}>
            <StyledContentText
              html={text}
              mobileReadMore={mobileReadMore}
              showMoreContent={showMoreContent}
            />
            {mobileReadMore && (
              <StyledContentButton
                onClick={() => setShowMoreContent(!showMoreContent)}
              >
                {!showMoreContent ? '+ Read More' : '- Read Less'}
              </StyledContentButton>
            )}
          </StyledContent>
        )}
      </Container>
    </StyledIconContent>
  );
};

export default IconContent;
